import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  FormControl,
  Select,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { getSystemPOsWithInvoices } from './functions/get_system_pos_with_invoices.function';
import { getSystemPOsWithNonPendingInvoicesWithin6Months } from './functions/get_system_pos_with_non_pending_invoices_within_6_months.function';
import { getSystemPOsWithNonPendingInvoicesOlderThan6Months } from './functions/get_system_pos_with_non_pending_invoices_older_than_6_months.function';
import { getSystemPOsWithStoppedInvoices } from './functions/get_system_pos_with_stopped_invoices.function';
import { getAllSystemVendorInvoicesForWorkflow } from './functions/get_all_system_vendor_invoices.function';
import { getSystemPoItemDetailsByPoId } from './functions/get_system_po_item_details_by_po_id.function';

const formatDate = (dateString) => {
  if (!dateString) {
    return '';
  }
  const d = dayjs(dateString);
  return d.isValid() ? d.format('YYYY-MM-DD') : String(dateString);
};

const statusLabel = (raw) => {
  const s = String(raw || '')
    .toLowerCase()
    .trim();
  if (!s) {
    return 'N/A';
  }
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const SystemPOWorkflowTable = ({ selectedTab }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [loading, setLoading] = useState(false);
  const [workflowData, setWorkflowData] = useState([]);
  const [allInvoicesWorkflowData, setAllInvoicesData] = useState([]);
  const [poItemsModal, setPoItemsModal] = useState({
    open: false,
    poData: null,
    items: [],
    loading: false,
  });

  useEffect(() => {
    const fetchPOs = async () => {
      setLoading(true);
      try {
        if (selectedTab === 'all-invoices-full') {
          const invoices = await getAllSystemVendorInvoicesForWorkflow();
          setAllInvoicesData(Array.isArray(invoices) ? invoices : []);
          setWorkflowData([]);
          return;
        }

        let response = [];
        if (selectedTab === 'action-required') {
          response = await getSystemPOsWithInvoices();
        } else if (selectedTab === 'stopped-invoices') {
          response = await getSystemPOsWithStoppedInvoices();
        } else if (selectedTab === 'all-invoices') {
          response = await getSystemPOsWithNonPendingInvoicesWithin6Months();
        } else if (selectedTab === 'archive') {
          response = await getSystemPOsWithNonPendingInvoicesOlderThan6Months();
        } else {
          response = await getSystemPOsWithInvoices();
        }

        const poMap = new Map();
        (response || []).forEach((row) => {
          const poNumber = row?.po || row?.po_number;
          if (!poNumber) {
            return;
          }
          if (!poMap.has(poNumber)) {
            poMap.set(poNumber, {
              poDetails: {
                id: row.po_id,
                po: row.po,
                po_date: row.po_date,
                vendor: row.vendor,
                total_amount: row.total_amount,
                po_delivery_date: row.po_delivery_date,
              },
              invoices: [],
            });
          }
          const entry = poMap.get(poNumber);
          entry.invoices.push({
            invoiceDetails: {
              id: row.invoice_id,
              invoice_number: row.invoice_number,
              invoice_amount: row.invoice_amount,
              vendor: row.invoice_vendor,
              region: row.region,
              status: row.status,
              created_at: row.invoice_created_at,
              category: row.category,
              invoice_type: row.invoice_type,
              // PO/system claim type used for Planning L2 routing (IPRAN/OTN/FTTS)
              type: row.invoice_type_detail || row.type || null,
              service_items_data: row.service_items_data,
              sites: row.sites,
              other_documents_paths: row.other_documents_paths,
              po_file_path: row.po_file_path,
              po_number: row.po_number,
              po_date: row.invoice_po_date,
              deliver_date: row.deliver_date,
              remaining_po_amount: row.remaining_po_amount,
              vendor_remarks: row.vendor_remarks,
              reason_of_rejection: row.reason_of_rejection,
              assigned_to_role_id: row.assigned_to_role_id,
              previous_assigned_to_roole_id: row.previous_assigned_to_roole_id,
              previous_assigned_role: row.prev_role_id
                ? { id: row.prev_role_id, name: row.prev_role_name }
                : null,
            },
            assignedRole: row.role_id
              ? {
                  id: row.role_id,
                  name: row.role_name,
                  category: row.role_category,
                  region: row.role_region,
                  invoice_type: row.role_invoice_type,
                  is_active: row.role_is_active,
                }
              : null,
          });
        });

        const mapped = Array.from(poMap.values()).map((entry) => {
          const poDetails = entry.poDetails;
          const invoices = entry.invoices;
          return {
            id: poDetails.id,
            poNumber: poDetails.po,
            poDate: formatDate(poDetails.po_date),
            vendor: poDetails.vendor || 'N/A',
            totalAmount: poDetails.total_amount || '0',
            deliveryDate: formatDate(poDetails.po_delivery_date),
            invoiceCount: invoices.length,
            // Keep assignedRole alongside invoiceDetails for details page
            _invoices: invoices,
            _poDetails: poDetails,
          };
        });

        setWorkflowData(mapped);
      } finally {
        setLoading(false);
      }
    };
    fetchPOs();
  }, [selectedTab]);

  const handleOpenPOInvoices = useCallback(
    (poData) => {
      const invoices = (poData._invoices || []).map((item) => {
        const inv = item.invoiceDetails || item;
        const assignedRole = item.assignedRole || item.assigned_role || inv.assigned_role || null;
        const previousRole = inv.previous_assigned_role || item.previous_assigned_role || null;
        const safeAssignedRole = assignedRole?.id || assignedRole?.name ? assignedRole : null;
        const safePreviousRole = previousRole?.id || previousRole?.name ? previousRole : null;
        return {
          invoiceNumber: inv.invoice_number || '—',
          invoiceDate: formatDate(inv.created_at),
          invoiceAmount: inv.invoice_amount || 0,
          approvalStatus: statusLabel(inv.status),
          statusRaw: String(inv.status || '')
            .toLowerCase()
            .trim(),
          vendor: inv.vendor || '—',
          region: inv.region || '—',
          currentApprover: safeAssignedRole?.name || '',
          previousApprover: safePreviousRole?.name || '',
          assigned_role: safeAssignedRole,
          previous_assigned_role: safePreviousRole,
          _originalData: {
            ...inv,
            type: inv.type || inv.invoice_type_detail || null,
            invoice_type: inv.invoice_type || null,
            assigned_role: safeAssignedRole,
            previous_assigned_role: safePreviousRole,
          },
        };
      });
      navigate('/systemPoWorkflowManagement/invoices', {
        state: {
          poNumber: poData.poNumber,
          invoices,
          poDetails: poData._poDetails || null,
        },
      });
    },
    [navigate]
  );

  const handleOpenPoItemsDetails = useCallback(async (poData) => {
    if (!poData?.id) {
      return;
    }
    setPoItemsModal({ open: true, poData, items: [], loading: true });
    try {
      const items = await getSystemPoItemDetailsByPoId(poData.id);
      setPoItemsModal({
        open: true,
        poData,
        items: Array.isArray(items) ? items : [],
        loading: false,
      });
    } catch {
      setPoItemsModal({ open: true, poData, items: [], loading: false });
    }
  }, []);

  const handleClosePoItemsDetails = useCallback(() => {
    setPoItemsModal({ open: false, poData: null, items: [], loading: false });
  }, []);

  const handleOpenInvoiceDetailsFromAll = useCallback(
    (rowData) => {
      const rawInvoice = rowData;
      const invoiceData = {
        ...rawInvoice,
        type: rawInvoice.type || rawInvoice.invoice_type_detail || null,
        invoice_type: rawInvoice.invoice_type || null,
        assigned_role: rawInvoice.role_id
          ? {
              id: rawInvoice.role_id,
              name: rawInvoice.role_name,
              category: rawInvoice.role_category,
              region: rawInvoice.role_region,
              invoice_type: rawInvoice.role_invoice_type,
              is_active: rawInvoice.role_is_active,
            }
          : rawInvoice.assigned_role || null,
        previous_assigned_role: rawInvoice.prev_role_id
          ? { id: rawInvoice.prev_role_id, name: rawInvoice.prev_role_name }
          : rawInvoice.previous_assigned_role || null,
      };
      navigate('/systemPoWorkflowManagement/invoices/details', {
        state: {
          invoiceData,
          poNumber: rawInvoice.po_number || '',
          invoices: [],
          poDetails:
            rawInvoice.total_amount != null ? { total_amount: rawInvoice.total_amount } : null,
          fromTab: 'all-invoices-full',
        },
      });
    },
    [navigate]
  );

  const vendors = useMemo(() => {
    const unique = [...new Set(workflowData.map((i) => i.vendor).filter(Boolean))];
    return unique.sort();
  }, [workflowData]);

  const cities = useMemo(() => {
    const unique = [...new Set(workflowData.map((i) => i.city).filter(Boolean))];
    return unique.sort();
  }, [workflowData]);

  const dates = useMemo(() => {
    const unique = [...new Set(workflowData.map((i) => i.poDate).filter(Boolean))];
    return unique.sort();
  }, [workflowData]);

  const poColumns = useMemo(
    () => [
      {
        accessorKey: 'poNumber',
        header: 'PO Number',
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell, row }) => (
          <Button
            variant="text"
            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPOInvoices(row.original);
            }}
          >
            {cell.getValue() || 'N/A'}
          </Button>
        ),
      },
      { accessorKey: 'vendor', header: 'Vendor' },
      { accessorKey: 'poDate', header: 'PO Date' },
      { accessorKey: 'deliveryDate', header: 'Delivery Date' },
      { accessorKey: 'totalAmount', header: 'Total Amount' },
      { accessorKey: 'invoiceCount', header: 'Invoices' },
    ],
    [handleOpenPOInvoices]
  );

  const invoiceColumns = useMemo(
    () => [
      { accessorKey: 'invoice_number', header: 'Invoice Number' },
      { accessorKey: 'po_number', header: 'PO' },
      { accessorKey: 'vendor', header: 'Vendor' },
      { accessorKey: 'region', header: 'Region' },
      { accessorKey: 'invoice_amount', header: 'Amount' },
      { accessorKey: 'status', header: 'Status' },
    ],
    []
  );

  const filteredData = useMemo(() => {
    if (selectedTab === 'all-invoices-full') {
      let filtered = allInvoicesWorkflowData;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        filtered = filtered.filter((item) =>
          Object.values(item).some((val) => val != null && String(val).toLowerCase().includes(s))
        );
      }
      return filtered;
    }

    let filtered = workflowData;
    if (selectedDate) {
      filtered = filtered.filter((item) => item.poDate === selectedDate);
    }
    if (selectedCity) {
      filtered = filtered.filter((item) => item.city === selectedCity);
    }
    if (selectedVendor) {
      filtered = filtered.filter((item) => item.vendor === selectedVendor);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) => val != null && String(val).toLowerCase().includes(s))
      );
    }
    return filtered;
  }, [
    selectedTab,
    allInvoicesWorkflowData,
    workflowData,
    selectedDate,
    selectedCity,
    selectedVendor,
    searchTerm,
  ]);

  const handleDownloadExcel = useCallback(
    (tableInstance) => {
      let dataToDownload = filteredData;
      if (tableInstance) {
        const filteredRows = tableInstance.getFilteredRowModel().rows;
        dataToDownload = filteredRows.map((row) => row.original);
      }
      if (!Array.isArray(dataToDownload) || dataToDownload.length === 0) {
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(dataToDownload);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'system_workflow');
      const currentDate = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `System_PO_Workflow_${selectedTab}_${currentDate}.xlsx`);
    },
    [filteredData, selectedTab]
  );

  // eslint-disable-next-line react/prop-types
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);
    const handleOpen = (event) => {
      event?.stopPropagation?.();
      handleClose();
      handleOpenPOInvoices(row.original);
    };
    const handleOpenPoItems = (event) => {
      event?.stopPropagation?.();
      handleClose();
      handleOpenPoItemsDetails(row.original);
    };
    return (
      <>
        <IconButton size="small" onClick={handleClick} sx={{ color: '#667085' }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={(e) => {
            e?.stopPropagation?.();
            handleClose();
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleOpen(e);
            }}
            sx={{ gap: 1 }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleOpenPoItems(e);
            }}
            sx={{ gap: 1 }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PO & items details</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  };

  const tableColumns =
    selectedTab === 'all-invoices-full'
      ? invoiceColumns
      : [
          ...poColumns,
          {
            header: 'Actions',
            enableColumnActions: false,
            size: 50,
            // eslint-disable-next-line react/prop-types
            Cell: ({ row }) => <ActionsMenu row={row} />,
          },
        ];

  const table = useMaterialReactTable({
    columns: tableColumns,
    data: Array.isArray(filteredData) ? filteredData : [],
    state: { isLoading: loading },
    enableSorting: false,
    enableDensityToggle: false,
    enableColumnOrdering: false,
    enableGlobalFilter: false,
    muiTableBodyRowProps:
      selectedTab === 'all-invoices-full'
        ? ({ row }) => ({
            onClick: () => handleOpenInvoiceDetailsFromAll(row.original),
            sx: { cursor: 'pointer' },
          })
        : ({ row }) => ({
            onClick: () => handleOpenPOInvoices(row.original),
            sx: { cursor: 'pointer' },
          }),
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side - Search field */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: '300px',
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Right side - Filters and download icon */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {selectedTab !== 'all-invoices-full' ? (
            <>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#757575',
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '&:hover': {
                      border: '1px solid #ccc',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #ccc',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      borderBottom: '1px solid #eee',
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                    }}
                  >
                    CITY
                  </MenuItem>
                  {cities.map((city, index) => (
                    <MenuItem
                      key={city}
                      value={city}
                      sx={{
                        fontSize: '13px',
                        color: '#656e7a',
                        lineHeight: 1.4,
                        borderBottom: index !== cities.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#757575',
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '&:hover': {
                      border: '1px solid #ccc',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #ccc',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      borderBottom: '1px solid #eee',
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                    }}
                  >
                    DATE
                  </MenuItem>
                  {dates.map((date, index) => (
                    <MenuItem
                      key={date}
                      value={date}
                      sx={{
                        fontSize: '13px',
                        color: '#656e7a',
                        lineHeight: 1.4,
                        borderBottom: index !== dates.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {date}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#757575',
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '&:hover': {
                      border: '1px solid #ccc',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #ccc',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      borderBottom: '1px solid #eee',
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                    }}
                  >
                    VENDOR
                  </MenuItem>
                  {vendors.map((vendor, index) => (
                    <MenuItem
                      key={vendor}
                      value={vendor}
                      sx={{
                        fontSize: '13px',
                        color: '#656e7a',
                        lineHeight: 1.4,
                        borderBottom: index !== vendors.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {vendor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null}

          <Button
            variant="outlined"
            onClick={() => handleDownloadExcel(table)}
            disabled={filteredData.length === 0}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              minWidth: 'auto',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid #ccc',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#999',
                opacity: 0.6,
              },
            }}
          >
            <DownloadIcon />
          </Button>
        </Box>
      </Box>
    ),
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
  });

  return (
    <Box sx={{ width: '100%' }}>
      <MaterialReactTable table={table} />

      <Dialog
        open={poItemsModal.open}
        onClose={handleClosePoItemsDetails}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
            PO Details & Item Details — {poItemsModal.poData?.poNumber || '—'}
          </Typography>
          <IconButton size="small" onClick={handleClosePoItemsDetails} title="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            PO Information
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 1.5,
              mb: 3,
              p: 1.5,
              bgcolor: '#fafafa',
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                PO Number
              </Typography>
              <br />
              <Typography variant="body2">{poItemsModal.poData?.poNumber || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                PO Date
              </Typography>
              <br />
              <Typography variant="body2">{poItemsModal.poData?.poDate || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Vendor
              </Typography>
              <br />
              <Typography variant="body2">{poItemsModal.poData?.vendor || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Amount
              </Typography>
              <br />
              <Typography variant="body2">{poItemsModal.poData?.totalAmount || 'N/A'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Delivery Date
              </Typography>
              <br />
              <Typography variant="body2">{poItemsModal.poData?.deliveryDate || 'N/A'}</Typography>
            </Box>
          </Box>

          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Item Details
          </Typography>

          {poItemsModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 56 }}>Sr</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Item Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Unit Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(poItemsModal.items || []).map((it, idx) => (
                    <TableRow key={`${it?.item_code || 'item'}-${idx}`}>
                      <TableCell sx={{ color: '#667085' }}>{idx + 1}</TableCell>
                      <TableCell>{it?.item_code || '—'}</TableCell>
                      <TableCell>{it?.item_type || '—'}</TableCell>
                      <TableCell align="right">{it?.quantity || '—'}</TableCell>
                      <TableCell align="right">{it?.unit_price || '—'}</TableCell>
                      <TableCell>{it?.um || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {(!poItemsModal.items || poItemsModal.items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ color: '#667085' }}>
                        No item details found for this PO.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePoItemsDetails} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
